<template>
  <div v-if="isFeatureFlagsVisible && error === null" v-loading="loading">
    <rest-form
      :disabled="isFeatureFlagsDisabled"
      ref="form"
      :model="value"
      :after-save="onFeaturesSaved"
      :save-endpoint="saveEndpoint"
    >
      <form-section :title="sectionTitle">
        <template #controls>
          <save-button
            :click="onFormSaveButtonClicked"
            data-qa="mxa-features-save-button"
          />
        </template>

        <el-row>
          <el-col v-for="key in sortedFeatureKeys" :key="key" :span="12">
            <form-item
              :prop="key"
              :label="getFeatureTitle(key)"
              :description="getFeatureDescription(key)"
            >
              <feature-toggle
                v-if="isBooleanFeature(key)"
                :value="getFeatureValue(key)"
                @input="newValue => setFeature(key, newValue)"
                :active-value="true"
                :inactive-value="false"
                :data-qa="getFeatureQaLabel(key)"
              />

              <div v-else>UNKNOWN FLAG TYPE</div>
            </form-item>
          </el-col>
        </el-row>
      </form-section>
    </rest-form>
    <mxa-save-features-dialog
      ref="saveDialog"
      :feature-defs="featureDefinitions"
      :feature-values="value"
      :feature-properties="featureProperties"
      :original-feature-values="originalValue"
      :changed-features="changedFeatures"
      @save="onSaveConfirmed"
      @close="onSaveCancelled"
    />
  </div>
</template>

<script>
import ElRow from '@/common/components/element/ElRow.vue'
import FeatureToggle from '@/common/components/form/FeatureToggle.vue'
import FormItem from '@/common/components/form/FormItem.vue'
import FormSection from '@/common/components/form/FormSection.vue'
import SaveButton from '@/common/components/form/SaveButton.vue'
import RestForm from '@/common/components/rest/RestForm.vue'

import mxaFeatureProperties from '@/modules/project/data/mxa-feature-properties'
import { projectEndpoint } from '@/core/helpers'
import MxaSaveFeaturesDialog from '@/modules/project/components/MxaSaveFeaturesDialog.vue'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'MxaFeatureFlags',
  components: {
    MxaSaveFeaturesDialog,
    FeatureToggle,
    FormItem,
    ElRow,
    FormSection,
    RestForm,
    SaveButton
  },

  data() {
    return {
      featureDefinitions: null,
      featureProperties: mxaFeatureProperties,
      error: null,
      loading: false,
      value: null,
      originalValue: null
    }
  },

  mounted() {
    this.loadMxaFeatures()
  },

  computed: {
    changedFeatures() {
      if (this.value === null || this.originalValue === null) {
        return []
      }

      let changed = []

      for (const key of this.sortedFeatureKeys) {
        if (this.value[key] !== this.originalValue[key]) {
          changed.push(key)
        }
      }

      return changed
    },
    hasChangedFeatures() {
      return this.changedFeatures.length > 0
    },
    isFeatureFlagsDisabled() {
      return !this.$can('ProjectFeatureFlags::Manage')
    },
    isFeatureFlagsVisible() {
      return this.$can('ProjectFeatureFlags::Read')
    },
    saveEndpoint() {
      return projectEndpoint({
        method: 'PUT',
        url: 'mxa/features'
      })
    },
    sectionTitle() {
      return this.$t('ProjectFeatures_MXA_Features')
    },
    sortedFeatureKeys() {
      if (this.value === null) {
        return null
      }

      // Put the liveEditJourneys toggle behind a release flag check.
      // This is temporary and will be remove in XP-12914.
      return Object.keys(this.value)
        .filter(
          key =>
            key !== 'liveEditJourneys' ||
            this.$hasReleaseFlag('live-edit-journeys')
        )
        .sort()
    }
  },

  methods: {
    getFeatureDescription(key) {
      if (!(key in this.featureProperties)) {
        throw new Error('tried to look up non-existent property set')
      }

      if (!('description' in this.featureProperties[key])) {
        throw new Error('tried to look up non-existent description translation')
      }

      return this.$t(this.featureProperties[key].description)
    },
    getFeatureQaLabel(key) {
      if (!(key in this.featureProperties)) {
        throw new Error('tried to look up non-existent property set')
      }

      if (!('dataQa' in this.featureProperties[key])) {
        throw new Error('tried to look up non-existent data-qa label')
      }

      return this.featureProperties[key].dataQa
    },
    getFeatureTitle(key) {
      if (!(key in this.featureProperties)) {
        throw new Error('tried to look up non-existent property set')
      }

      if (!('title' in this.featureProperties[key])) {
        throw new Error('tried to look up non-existent title translation')
      }

      return this.$t(this.featureProperties[key].title)
    },
    getFeatureValue(key) {
      if (this.value === null || !(key in this.value)) {
        return null
      }

      return this.value[key]
    },
    isBooleanFeature(key) {
      if (!(key in this.featureDefinitions)) {
        throw new Error('tried to look up non-existent feature key')
      }

      if (!('type' in this.featureDefinitions[key])) {
        throw new Error('type property does not exist on feature')
      }

      return this.featureDefinitions[key].type === 'bool'
    },
    loadMxaFeatures() {
      let endpoint = projectEndpoint({
        method: 'get',
        url: 'mxa/features'
      })

      this.loading = true
      RestApi(endpoint)
        .then(({ data }) => {
          this.featureDefinitions = data

          let values = {}
          for (const key in this.featureDefinitions) {
            values[key] = this.featureDefinitions[key].value
          }

          this.value = values
          this.originalValue = { ...this.value }
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.error = error

          this.$message.error('Failed to load MXA feature flags')
        })
    },
    onFeaturesSaved(_response, finishLoadingFn) {
      finishLoadingFn()
      if (this.changedFeatures.includes('liveEditJourneys')) {
        this.$router.go()
        return
      }
      this.loadMxaFeatures()
    },
    onFormSaveButtonClicked() {
      if (this.hasChangedFeatures) {
        this.$refs.saveDialog.setVisible(true)
      } else {
        this.$refs.form.save()
      }
    },
    onSaveCancelled() {
      this.$refs.saveDialog.setVisible(false)
    },
    onSaveConfirmed() {
      this.$refs.saveDialog.setVisible(false)
      this.$refs.form.save()
    },
    setFeature(key, newValue) {
      if (this.value === null || !(key in this.value)) {
        return
      }

      this.value[key] = newValue
    }
  }
}
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/modules/project/components/MxaFeatureFlags.vue